import { Clipboard } from '@angular/cdk/clipboard';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { Subscription, map } from 'rxjs';
import { VideoDTO } from 'src/app/common/dtos/video.dto';
import { UserRole } from 'src/app/common/state/user/role/user-role';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import {
  EvidenceAssessedDTO,
  EvidenceSummaryAttachmentResponse,
  EvidenceSummaryItemResponse,
} from 'src/app/common/types/responses/responses';
import { Animations } from 'src/app/common/utilities/animations';
import { select } from 'src/app/common/utilities/ngxs-utils';
import {
  checkIfCoach,
  checkIfShadower,
} from 'src/app/common/utilities/session-helpers';
import { EvidenceDTO } from '../../dtos/attendee-rubric.dto';
import { CoachingSessionDTO } from '../../dtos/coaching-session.dto';
import { FileDTO } from '../../dtos/file.dto';
import { fileDTOFromEvidenceSummaryAttachmentResponse } from '../../helpers/translators/evidence.translators';
import { videoDTOFromAPIResponse } from '../../helpers/translators/video.translators';
import { InstantFeedbackService } from '../../services/ai/instant-feedback.service';
import { CoachingLogEvidenceService } from '../../services/coaching-log/coaching-log-evidence.service';
import { EvidenceService } from '../../services/evidence/evidence.service';
import { UserPusherService } from '../../services/pusher/user.pusher.service';
import { FEATURE_FLAGS } from '../../services/unleash/unleash.helpers';
import { UnleashService } from '../../services/unleash/unleash.service';
import { CoachingLogState } from '../../state/coaching-log/coaching-log.state';
import { SmartFeedbackResponse } from '../../types/responses/instant-feedback.responses';

import { CommentModelDTO } from '../../dtos/comment.dto';
import { CommentsService } from '../../services/comments/comments.service';
import { CopyEvidenceModalComponent } from '../modals/copy-evidence-modal/copy-evidence-modal.component';
import { ModalComponent, ModalConfig } from '../modals/modal/modal.component';

@Component({
  selector: 'app-evidence-summary',
  templateUrl: './evidence-summary.component.html',
  styleUrls: ['./evidence-summary.component.scss'],
  standalone: false,
  animations: [Animations.deelaayfadeInOut],
})
export class EvidenceSummaryComponent implements OnInit, OnDestroy {
  @ViewChild('copyEvidenceModal') copyEvidenceModal: CopyEvidenceModalComponent;

  @Input() evidenceId: number;

  @Input() sessionId: number;

  @Input() userId: number;

  @Input() autoOpenModal: boolean;

  @Output() readonly evidenceAssesed: EventEmitter<EvidenceAssessedDTO> =
    new EventEmitter();

  @Output() readonly evidenceRemoved: EventEmitter<number> = new EventEmitter();

  sessionData$ = select(CoachingLogState.getSessionData);

  sessionData: CoachingSessionDTO | null;

  subs: Subscription[] = [];

  loadingData = true;

  currentUser: User | null;

  isCoach = false;

  isShadower = false;

  evidence: EvidenceDTO;

  evidenceSummary: EvidenceSummaryItemResponse;

  modalConfig: ModalConfig;

  hasAttachments = false;

  uploadedFiles: FileDTO[] = [];

  commentList: CommentModelDTO[];

  commentRequest: Subscription;

  evidenceAssessing = false;

  evidenceAssessed = false;

  video: VideoDTO;

  evidenceData$ = select(CoachingLogState.getOneEvidence);

  copyEvidenceEnabled = false;

  instantFeedbackEnabled = false;

  smartFeedback: SmartFeedbackResponse | undefined;

  smartFeedbackId: number;

  smartFeedbackLoading = false;

  errorMessage: string;

  loadingMessage: string;

  copiedStatus = false;

  @ViewChild('evidenceSummaryModal') evidenceSummaryModal: ModalComponent;

  constructor(
    private evidenceService: EvidenceService,
    private commentsService: CommentsService,
    private coachingLogEvidenceService: CoachingLogEvidenceService,
    private featureFlagService: UnleashService,
    private instantFeedbackService: InstantFeedbackService,
    private userPusherService: UserPusherService,
    private store: Store,
    private clipboard: Clipboard
  ) {
    this.currentUser = this.store.selectSnapshot(UserState.getUser);
  }

  ngOnInit(): void {
    this.getEvidenceSummary();

    this.copyEvidenceEnabled = this.featureFlagService.isEnabled(
      FEATURE_FLAGS.copyEvidence
    );

    this.modalConfig = {
      headerClass: ['modal-header', 'text-white', 'text-center'],
      closeButton: {
        classes: ['btn', 'btn-md', 'text-white', 'fw-800', 'text-uppercase'],
        label: 'Submit',
      },
      customFooter: true,
      titleText: '',
      options: {
        size: 'lg',
      },
    };

    if (this.sessionId) {
      this.sessionData$
        .pipe(
          map((filterFn) =>
            (filterFn as (id: number) => CoachingSessionDTO)(this.sessionId)
          )
        )
        .pipe(
          map((sessionData) => {
            if (sessionData) {
              this.sessionData = sessionData;
              this.isShadower = checkIfShadower(this.sessionData);
              this.isCoach = checkIfCoach(this.sessionData);
            }
          })
        )
        .subscribe();
    } else {
      this.checkIfCoach();
    }

    this.evidenceData$
      .pipe(
        map((filterFn) =>
          (filterFn as (evidenceId: number) => EvidenceDTO[])(this.evidenceId)
        )
      )
      .subscribe(() => {
        // Must restrict or all evidences will update
        // Do not reset evidence if autoOpen - where page content
        // does not need to update again.
        if (!this.evidenceAssessed && !this.autoOpenModal) {
          this.getEvidenceSummary();
        }
      });

    this.subs.push(
      this.userPusherService.feedbackUpdatedNotifier.subscribe((feedback) => {
        if (this.smartFeedbackId === feedback.feedbackId) {
          if (
            feedback.smartFeedback.strengths ||
            feedback.smartFeedback.suggestions ||
            feedback.smartFeedback.improvements ||
            feedback.smartFeedback.examples ||
            feedback.smartFeedback.error
          ) {
            this.loadingMessage = '';
            this.smartFeedback = feedback.smartFeedback;
            this.smartFeedbackLoading = false;
          } else {
            this.loadingMessage = '';
            this.errorMessage =
              'No feedback available. Please edit your evidence, add more context, and try again.';
            this.smartFeedbackLoading = false;
          }
        }
      })
    );
  }

  checkIfCoach() {
    if (this.currentUser) {
      if (
        this.currentUser.roles.includes(UserRole.COACH) ||
        this.currentUser.roles.includes(UserRole.E2L_EMPLOYEE)
      ) {
        this.isCoach = true;
      }
    }
  }

  getEvidenceSummary() {
    this.evidenceService
      .getEvidenceSummary(this.evidenceId)
      .subscribe((res) => {
        this.evidenceSummary = res.item;
        this.instantFeedbackEnabled =
          this.featureFlagService.isEnabled(
            FEATURE_FLAGS.instantFeedbackCard
          ) && this.evidenceSummary.user_id === this.currentUser?.id;
        if (
          this.instantFeedbackEnabled &&
          (this.evidenceSummary.aiFeedback?.strengths ||
            this.evidenceSummary.aiFeedback?.suggestions ||
            this.evidenceSummary.aiFeedback?.improvements ||
            this.evidenceSummary.aiFeedback?.examples ||
            this.evidenceSummary.aiFeedback?.error)
        ) {
          this.smartFeedback = this.evidenceSummary.aiFeedback;
          this.smartFeedbackId = this.evidenceSummary.aiFeedback.id;
        }

        if (
          this.evidenceSummary.assessments &&
          this.evidenceSummary.assessments.length > 0
        ) {
          this.evidenceAssessed = true;
        }
        if (this.evidenceSummary.evidenceAttachments.length > 0) {
          this.evidenceSummary.evidenceAttachments.forEach(
            (e: EvidenceSummaryAttachmentResponse) => {
              const fileExists = this.uploadedFiles.some(
                (file) => file.id === e.id
              );
              if (!fileExists) {
                this.uploadedFiles.push(
                  fileDTOFromEvidenceSummaryAttachmentResponse(e)
                );
              }
            }
          );
          this.hasAttachments = true;
        }
        if (this.evidenceSummary.videos.length > 0) {
          this.video = videoDTOFromAPIResponse(this.evidenceSummary.videos[0]);
        }
        this.getComments();

        if (this.autoOpenModal) {
          this.showEvidenceModal();
        }

        this.loadingData = false;
      });
  }

  showEvidenceModal() {
    this.evidenceSummaryModal.config.titleText = this.evidenceSummary.title;
    this.evidenceSummaryModal.config.headerClass.push(
      `bg-level-${this.evidenceSummary.level}`
    );

    this.evidenceSummaryModal.open();

    if (this.autoOpenModal && this.coachingLogEvidenceService.evidenceId) {
      this.evidenceSummaryModal.isClosing.subscribe(() => {
        this.coachingLogEvidenceService.setEvidenceId();
      });
    }
  }

  closeEvidenceModal() {
    this.evidenceSummaryModal.close();
  }

  deleteEvidence() {
    this.evidenceService
      .deleteEvidence(this.evidenceSummary.id)
      .subscribe(() => {
        this.evidenceRemoved.emit(this.evidenceSummary.id);
        this.evidenceSummaryModal.close();
      });
  }

  assessEvidence() {
    this.evidenceAssessing = true;
    const payload = {
      ...this.evidenceSummary,
      pusher_coachlog_id: this.sessionId ? this.sessionId : null,
    };

    this.evidenceService.approveEvidence(payload).subscribe((res) => {
      if (res.item) {
        this.evidenceAssesed.next(res.item);
        this.evidenceAssessing = false;
        this.evidenceAssessed = true;
        this.evidenceSummaryModal.close();
      }
    });
  }

  getComments() {
    if (this.commentRequest) this.commentRequest.unsubscribe();
    this.commentRequest = this.commentsService
      .getComments('egrowe_evidence', this.evidenceSummary.id)
      .subscribe((response) => {
        if (response) {
          this.commentList = response.items;
        }
      });

    this.subs.push(this.commentRequest);
  }

  openCopyEvidenceModal() {
    this.evidenceSummaryModal.close();
    this.copyEvidenceModal.modal.open();
  }

  getSmartFeedback() {
    this.smartFeedbackLoading = true;
    this.errorMessage = '';
    this.instantFeedbackService
      .getSmartFeedback(this.evidenceSummary.id)
      .subscribe({
        error: (error) => {
          this.errorMessage = error.error.message;
          this.smartFeedbackLoading = false;
        },
        next: (response) => {
          if ('error' in response) {
            this.errorMessage = response.error;
            this.smartFeedbackLoading = false;
          } else {
            this.beginLoadingMessages();
            this.smartFeedbackId = response.ai_instant_feedback_id;
          }
        },
      });
  }

  beginLoadingMessages() {
    this.setLoadingMessage(
      "We're crafting feedback just for you ✨🛠️. Feel free to explore elsewhere in GroweLab 🧭, your feedback will be right here when it's ready 📬.",
      5000
    );
    this.setLoadingMessage('', 19000);
    this.setLoadingMessage(
      "Hang tight—we're still whipping up your feedback 🧑‍🍳💬! Feel free to continue wandering around GroweLab or step away 🌀. It'll be ready for you soon 🎁.",
      20000
    );
  }

  setLoadingMessage(message: string, timeout: number) {
    setTimeout(() => {
      if (this.smartFeedbackLoading) {
        this.loadingMessage = message;
      }
    }, timeout);
  }

  submitUserFeedback(direction: string) {
    if (this.smartFeedback) {
      this.instantFeedbackService
        .submitUserFeedback(this.smartFeedbackId, direction)
        .subscribe((resp) => {
          if (resp) {
            if (this.smartFeedback) {
              this.smartFeedback.user_feedback = resp.user_feedback;
            }
          }
        });
    }
  }

  copySmartFeedback() {
    if (!this.smartFeedback) return;

    let feedbackText = '';

    // Add error section if it exists
    if (this.smartFeedback.error && this.smartFeedback.error.length > 0) {
      feedbackText += `Error\n${this.smartFeedback.error}\n\n`;
    }

    // Add strengths section if it exists
    if (
      this.smartFeedback.strengths &&
      this.smartFeedback.strengths.length > 0
    ) {
      feedbackText += `Strengths\n${this.smartFeedback.strengths}\n\n`;
    }

    // Add suggestions section if it exists
    if (
      this.smartFeedback.suggestions &&
      this.smartFeedback.suggestions.length > 0
    ) {
      feedbackText += `Suggestions\n${this.smartFeedback.suggestions}\n\n`;
    }

    // Add improvements section (labeled as "Next Steps" in HTML) if it exists
    if (
      this.smartFeedback.improvements &&
      this.smartFeedback.improvements.length > 0
    ) {
      feedbackText += `Next Steps\n${this.smartFeedback.improvements}\n\n`;
    }

    // Add examples section if it exists
    if (this.smartFeedback.examples && this.smartFeedback.examples.length > 0) {
      feedbackText += `Examples\n${this.smartFeedback.examples}\n\n`;
    }

    // Remove trailing newlines
    feedbackText = feedbackText.trim();

    this.clipboard.copy(feedbackText);

    this.copiedStatus = true;
    setTimeout(() => {
      this.copiedStatus = false;
    }, 3000);
  }

  ngOnDestroy() {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }
  }
}
